import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import {
	lazy,
	type ReactNode,
	Suspense,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { fetchBlobFromUrl } from "../../api/person/person-attachment";
import { triggerDownloadFromUrl } from "../../utils/attachment-download";
import ImageZoomDialog from "../common/image-zoom-dialog";

export interface ViewerAttachment {
	name: string;
	type: string;
	/** Authenticated endpoint that streams the bytes (any source). */
	downloadUrl: string;
}

export type AttachmentKind = "image" | "pdf" | "docx" | "unsupported";

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/;

/** Route by server Content-Type, falling back to file extension. */
export function detectKind(name: string, type: string): AttachmentKind {
	const lowerName = name.toLowerCase();
	const lowerType = (type || "").toLowerCase();
	if (lowerType.startsWith("image/") || IMAGE_EXT_RE.test(lowerName)) return "image";
	if (lowerType.includes("pdf") || lowerName.endsWith(".pdf")) return "pdf";
	if (lowerType.includes("wordprocessingml") || lowerName.endsWith(".docx"))
		return "docx";
	return "unsupported";
}

/** An object URL for the blob, revoked when it changes or unmounts. */
function useObjectUrl(blob: Blob | null): string {
	const [url, setUrl] = useState<string>("");
	useEffect(() => {
		if (!blob) {
			setUrl("");
			return;
		}
		const objectUrl = URL.createObjectURL(blob);
		setUrl(objectUrl);
		return () => URL.revokeObjectURL(objectUrl);
	}, [blob]);
	return url;
}

const PdfJsView = lazy(() => import("./pdf-js-view"));

interface DocxPreviewModule {
	renderAsync: (
		data: Blob,
		bodyContainer: HTMLElement,
		styleContainer?: HTMLElement,
		options?: { inWrapper?: boolean; className?: string }
	) => Promise<void>;
}

/** Renders a DOCX inline via docx-preview (loaded lazily to keep the bundle small). */
function DocxView({ blob }: { blob: Blob }) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [error, setError] = useState<string | null>(null);
	const [rendering, setRendering] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setError(null);
		setRendering(true);
		if (containerRef.current) containerRef.current.innerHTML = "";

		(async () => {
			try {
				const docxPreview = (await import("docx-preview")) as DocxPreviewModule;
				if (cancelled || !containerRef.current) return;
				await docxPreview.renderAsync(blob, containerRef.current, undefined, {
					inWrapper: false,
					className: "docx-preview",
				});
			} catch (err) {
				if (!cancelled) setError(String(err));
			} finally {
				if (!cancelled) setRendering(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [blob]);

	if (error)
		return (
			<Alert severity="error" data-component="docx-render-error">
				Failed to render: {error}
			</Alert>
		);

	return (
		<Box data-component="docx-view">
			{rendering && (
				<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
					<CircularProgress size={22} />
				</Box>
			)}
			<Box ref={containerRef} data-component="docx-preview-container" />
		</Box>
	);
}

interface AttachmentBlobState {
	blob: Blob | null;
	loading: boolean;
	error: string | null;
}

/** Fetches the attachment bytes once per attachment. */
function useAttachmentBlob(attachment: ViewerAttachment | null): AttachmentBlobState {
	const [blob, setBlob] = useState<Blob | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!attachment) return;
		let cancelled = false;
		setBlob(null);
		setError(null);
		setLoading(true);
		fetchBlobFromUrl(attachment.downloadUrl)
			.then(({ blob: fetched }) => {
				if (cancelled) return;
				setBlob(fetched);
				setLoading(false);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(String(err));
				setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [attachment]);

	return { blob, loading, error };
}

/** Renders already-fetched bytes as the kind-specific view. */
function AttachmentContentBody({
	attachment,
	blob,
	loading,
	error,
}: {
	attachment: ViewerAttachment;
} & AttachmentBlobState) {
	const kind = detectKind(attachment.name, attachment.type);
	const imageUrl = useObjectUrl(kind === "image" ? blob : null);

	return (
		<>
			{loading && (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress />
				</Box>
			)}
			{error && <Alert severity="error">Failed to load: {error}</Alert>}
			{!loading && !error && imageUrl && kind === "image" && (
				<Box
					component="img"
					src={imageUrl}
					alt={attachment.name}
					data-component="attachment-image-view"
					sx={{
						display: "block",
						maxWidth: "100%",
						maxHeight: "80vh",
						margin: "0 auto",
					}}
				/>
			)}
			{!loading && !error && blob && kind === "pdf" && (
				<Suspense
					fallback={
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								py: 4,
							}}
						>
							<CircularProgress />
						</Box>
					}
				>
					<PdfJsView blob={blob} />
				</Suspense>
			)}
			{!loading && !error && blob && kind === "docx" && <DocxView blob={blob} />}
			{!loading && !error && blob && kind === "unsupported" && (
				<Stack spacing={2} sx={{ py: 4, alignItems: "center" }}>
					<Typography color="text.secondary">
						No inline preview for this file type (
						{attachment.type || "unknown"}).
					</Typography>
					<Button
						variant="contained"
						startIcon={<DownloadIcon />}
						onClick={() =>
							triggerDownloadFromUrl(
								attachment.downloadUrl,
								attachment.name
							)
						}
						data-component="unsupported-download-button"
					>
						Download
					</Button>
				</Stack>
			)}
		</>
	);
}

/**
 * Inline attachment preview: fetches the blob and renders the kind-specific
 * view. Shared by the viewer dialog and inline surfaces (grading CV tab).
 */
export function AttachmentContentView({
	attachment,
}: {
	attachment: ViewerAttachment;
}) {
	const state = useAttachmentBlob(attachment);
	return <AttachmentContentBody attachment={attachment} {...state} />;
}

export function AttachmentViewerDialog({
	attachment,
	onClose,
}: {
	attachment: ViewerAttachment | null;
	onClose: () => void;
}) {
	const state = useAttachmentBlob(attachment);
	const kind = attachment
		? detectKind(attachment.name, attachment.type)
		: "unsupported";
	const imageUrl = useObjectUrl(kind === "image" ? state.blob : null);

	// An image opens the way a release-note image does: the picture alone,
	// sized to the window, a click closes it. Only a failed load gets the
	// framed dialog, for its error message.
	if (kind === "image" && !state.error) {
		return (
			<ImageZoomDialog
				open={attachment !== null}
				onClose={onClose}
				src={imageUrl}
				alt={attachment?.name ?? ""}
			/>
		);
	}

	return (
		<Dialog
			open={attachment !== null}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			// Fixed height for previews so the dialog doesn't grow page-by-page as
			// the PDF/DOCX renders (which thrashes the ResizeObserver width loop).
			slotProps={{
				paper: {
					sx: { height: kind === "unsupported" ? undefined : "90vh" },
				},
			}}
			data-component="attachment-viewer-dialog"
		>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
				<DescriptionIcon fontSize="small" />
				<Typography
					variant="subtitle1"
					component="span"
					sx={{
						flex: 1,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
				>
					{attachment?.name}
				</Typography>
				{attachment && (
					<Button
						size="small"
						startIcon={<DownloadIcon />}
						onClick={() =>
							triggerDownloadFromUrl(
								attachment.downloadUrl,
								attachment.name
							)
						}
						data-component="viewer-download-button"
					>
						Download
					</Button>
				)}
				<IconButton
					onClick={onClose}
					size="small"
					data-component="viewer-close-button"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers sx={{ scrollbarGutter: "stable" }}>
				{attachment && (
					<AttachmentContentBody attachment={attachment} {...state} />
				)}
			</DialogContent>
		</Dialog>
	);
}

/**
 * Hook that owns one viewer dialog. Render `element` once; call `open(att)` from
 * a View button. Lets both grading + timeline share the same dialog with no dup.
 */
export function useAttachmentViewer(): {
	open: (attachment: ViewerAttachment) => void;
	element: ReactNode;
} {
	const [attachment, setAttachment] = useState<ViewerAttachment | null>(null);
	const open = useCallback((a: ViewerAttachment) => setAttachment(a), []);
	const element = (
		<AttachmentViewerDialog
			attachment={attachment}
			onClose={() => setAttachment(null)}
		/>
	);
	return { open, element };
}
